
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { ColorRule } from '@/models/color_rule';
import { ColorRuleList } from '@/collections/color_rules';
import { MeasureModel } from '@/models/measure';
import { ReportModel } from '@/models/report';
import ActionButton from '@/components/ActionButton.vue';
import { compositionConfig, getAvailableCompositionKeys, CompositionDefinition } from '@/config/compositionConfig';

// Fields shown in summary card (to avoid duplication in row details table)
const SUMMARY_FIELDS = [
  'Item[Item Description]', // Product name
  'variant',                // Variant name
  'product_sku',           // SKU
  'product_id',            // Product ID
  'variant_id'             // Variant ID
];

@Component({
  components: {
    ActionButton,
  },
})
export default class RowDetailsDialog extends Vue {
  @Prop({ type: Boolean, required: true })
  public value!: boolean;

  @Prop({ type: Object, required: true })
  public drilldownItem!: any;

  @Prop({ type: Object, required: true })
  public measure!: MeasureModel;

  @Prop({ type: Object, required: true })
  public colorRules!: ColorRuleList;

  @Prop({ type: Object, required: true })
  public report!: ReportModel;

  @Prop({ type: Array, required: true })
  public selectingDimensions!: any[];

  public tabIndex: number = 0;

  get dialogVisible() {
    return this.value;
  }

  set dialogVisible(val: boolean) {
    this.$emit('input', val);
  }

  @Watch('value')
  onDialogOpen(newVal: boolean) {
    if (newVal) {
      // Reset to first tab when dialog opens
      this.tabIndex = 0;
    }
  }

  public get rowDetails(): Array<{code: string, name: string, value: any, dataType: string}> {
    if (!this.drilldownItem || !this.report || !this.report.columns) {
      return [];
    }

    const details: Array<{code: string, name: string, value: any, dataType: string}> = [];

    // Iterate through all columns in the report
    for (const column of this.report.columns) {
      // Skip actions column, image columns, summary fields, and undefined values
      if (column.code === 'actions' ||
          column.dataType === 'image' ||
          SUMMARY_FIELDS.includes(column.code) ||
          this.drilldownItem[column.code] === undefined) {
        continue;
      }

      details.push({
        code: column.code,
        name: column.name,
        value: this.drilldownItem[column.code],
        dataType: column.dataType || 'text'
      });
    }

    return details;
  }

  public get groupedRowDetails(): Array<{category: string, items: Array<{code: string, name: string, value: any, dataType: string}>}> {
    if (!this.measure || !this.measure.code) {
      return [];
    }

    // Get applicable composition keys for this measure
    const applicableKeys = getAvailableCompositionKeys(this.measure.code);

    // Build groups using composition config
    const groups: Array<{category: string, items: Array<{code: string, name: string, value: any, dataType: string}>}> = [];
    const assignedCodes = new Set<string>();

    // Process each applicable composition key
    for (const key of applicableKeys) {
      const composition = compositionConfig[key];
      if (!composition) {
        continue;
      }

      const groupItems: Array<{code: string, name: string, value: any, dataType: string}> = [];

      // Find matching columns for this composition
      for (const detail of this.rowDetails) {
        // Check if this column code is in the composition's constituent codes
        if (composition.constituentCodes.includes(detail.code) && !assignedCodes.has(detail.code)) {
          groupItems.push(detail);
          assignedCodes.add(detail.code);
        }
      }

      // Only add group if it has items
      if (groupItems.length > 0) {
        groups.push({
          category: composition.compositionName,
          items: groupItems
        });
      }
    }

    // Add "Other" category for unassigned columns
    const otherItems: Array<{code: string, name: string, value: any, dataType: string}> = [];
    for (const detail of this.rowDetails) {
      if (!assignedCodes.has(detail.code)) {
        otherItems.push(detail);
      }
    }

    if (otherItems.length > 0) {
      groups.push({
        category: 'Other',
        items: otherItems
      });
    }

    return groups;
  }

  public getIconForDataType(dataType: string): string {
    switch (dataType) {
      case 'currency':
        return 'mdi-currency-usd';
      case 'number':
        return 'mdi-numeric';
      case 'date':
        return 'mdi-calendar';
      case 'text':
        return 'mdi-text-box-outline';
      default:
        return 'mdi-information-outline';
    }
  }

  public get summaryInfo(): {image: string | null, details: Array<{name: string, value: any}>} {
    if (!this.drilldownItem || !this.report || !this.report.columns) {
      return { image: null, details: [] };
    }

    let imageUrl: string | null = null;
    const summaryDetails: Array<{name: string, value: any}> = [];

    for (const column of this.report.columns) {
      // Get image URL (prefer variant_image, fallback to product image)
      if (column.dataType === 'image' && this.drilldownItem[column.code]) {
        // Prioritize variant_image over product image
        if (column.code === 'variant_image') {
          imageUrl = this.drilldownItem[column.code];
        } else if (column.code === 'image' && !imageUrl) {
          imageUrl = this.drilldownItem[column.code];
        }
      }

      // Get key summary fields
      if (SUMMARY_FIELDS.includes(column.code) && this.drilldownItem[column.code] && this.drilldownItem[column.code] !== '-' && this.drilldownItem[column.code] !== 'N/A') {
        summaryDetails.push({
          name: column.name,
          value: this.drilldownItem[column.code]
        });
      }
    }

    return { image: imageUrl, details: summaryDetails };
  }

  public get colorRuleExplanation(): {text: string, color: string} | null {
    if (!this.drilldownItem || !this.colorRules || !this.measure) {
      return null;
    }

    // Check for row-level color rule
    const rowColor = this.colorRules.evaluateRow(this.drilldownItem, this.measure.code);
    if (rowColor && rowColor.coloringType === 'row') {
      // Find the matching rule
      const appliedRule = this.colorRules.items.find((rule) => {
        return rule.measure === this.measure.code &&
               rule.coloringType === 'row' &&
               rule.color === rowColor.color;
      });

      if (appliedRule) {
        return {
          text: this.formatColorRuleExplanation(appliedRule, this.drilldownItem),
          color: appliedRule.color
        };
      }
    }

    return null;
  }

  public formatColorRuleExplanation(rule: ColorRule, row: any): string {
    // Get column name from code
    const column = this.report?.columns.find(col => col.code === rule.columnCode);
    const columnName = column ? column.name : rule.columnCode;

    // Handle multi-column rules (e.g., GREATER_THAN between two columns)
    if (rule.secondColumnCode) {
      const secondColumn = this.report?.columns.find(col => col.code === rule.secondColumnCode);
      const secondColumnName = secondColumn ? secondColumn.name : rule.secondColumnCode;

      const value1 = row[rule.columnCode];
      const value2 = row[rule.secondColumnCode];

      switch (rule.operator) {
        case 'GREATER_THAN':
          return `This row is colored because ${columnName} (${value1}) is greater than ${secondColumnName} (${value2})`;
        case 'GREATER_AND_DIFF_PERCENT_GREATER':
          return `This row is colored because ${columnName} (${value1}) is greater than ${secondColumnName} (${value2}) by more than ${rule.value}%`;
        case 'GREATER_AND_DIFF_PERCENT_LESS':
          return `This row is colored because ${columnName} (${value1}) is greater than ${secondColumnName} (${value2}) by less than ${rule.value}%`;
        case 'PERCENTAGE_DIFFERENCE_BETWEEN':
          return `This row is colored because the percentage difference between ${columnName} (${value1}) and ${secondColumnName} (${value2}) is within the specified range`;
        default:
          return `This row is colored based on a comparison between ${columnName} and ${secondColumnName}`;
      }
    }

    // Single column rules
    const actualValue = row[rule.columnCode];

    // Handle array values (BETWEEN operator)
    if (Array.isArray(rule.value)) {
      const [min, max] = rule.value;
      if (rule.operator === 'BETWEEN') {
        return `This row is colored because ${columnName} (${actualValue}) is between ${min} and ${max}`;
      } else if (rule.operator === 'PERCENTAGE_DIFFERENCE_BETWEEN') {
        return `This row is colored because ${columnName} (${actualValue}) has a percentage difference between ${min}% and ${max}%`;
      }
    }

    // Single value operators
    switch (rule.operator) {
      case '>':
        return `This row is colored because ${columnName} (${actualValue}) is greater than ${rule.value}`;
      case '<':
        return `This row is colored because ${columnName} (${actualValue}) is less than ${rule.value}`;
      case '=':
        return `This row is colored because ${columnName} (${actualValue}) equals ${rule.value}`;
      case '>=':
        return `This row is colored because ${columnName} (${actualValue}) is greater than or equal to ${rule.value}`;
      case '<=':
        return `This row is colored because ${columnName} (${actualValue}) is less than or equal to ${rule.value}`;
      default:
        return `This row is colored based on ${columnName} (${actualValue})`;
    }
  }

  public closeDialog() {
    this.dialogVisible = false;
  }

  public selectDimension(dimensionCode: string) {
    this.$emit('select-dimension', dimensionCode);
  }
}
